import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getVariant, trackAb } from "../utils/abTest";
import {
  ArrowRight,
  Shield,
  Activity,
  Database,
  Fingerprint,
  Zap,
  FileCheck,
  Brain,
  Mic,
  Users,
  Play,
  ChevronDown,
  Handshake,
  Sparkles,
  Lock,
  Network,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { formatApiError } from "../utils/errorFormatter";
import SEO from "../components/SEO";

// One-click "use the real app, no login" — reuses the existing public demo
// session endpoint (ephemeral synthetic-data tenant, cookie set server-side).
const startDemoSession = async (source, setBusy) => {
  try {
    setBusy && setBusy(true);
    await axios.post(`${API}/demo/start`, { referral_source: source }, { withCredentials: true });
    toast.success("Launching your demo — 100% synthetic data, no login needed");
    window.location.href = "/app/dashboard";
  } catch (err) {
    toast.error(formatApiError(err, "Could not start the demo. Please try again."));
    setBusy && setBusy(false);
  }
};

// Reusable "Try it free — no login" button (self-contained loading state).
const TryFreeButton = ({ source = "landing", variant = "primary", className = "", label = "Try it free — no login", testid = "cta-try-free" }) => {
  const [busy, setBusy] = useState(false);
  const base = "font-semibold rounded-full text-sm transition-all inline-flex items-center gap-2 disabled:opacity-70";
  const styles = variant === "primary"
    ? "bg-[#00B389] hover:bg-[#009774] text-white px-7 py-3.5 shadow-[0_10px_30px_-8px_rgba(0,179,137,0.5)] hover:shadow-[0_14px_36px_-8px_rgba(0,179,137,0.6)]"
    : variant === "nav"
    ? "bg-[#00B389] hover:bg-[#009774] text-white px-5 py-2"
    : "bg-white border border-slate-200 hover:border-[#00B389] text-[#0B1220] px-7 py-3.5 shadow-sm";
  return (
    <button
      type="button"
      onClick={() => startDemoSession(source, setBusy)}
      disabled={busy}
      className={`${base} ${styles} ${className}`}
      data-testid={testid}
    >
      {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <><Play className="w-4 h-4" /> {label}</>}
    </button>
  );
};

// ============================================================
// LANDING PAGE v2 — "Clinical Light" aesthetic (2026-02-27 refresh)
// User asked for LIGHT so it's visible.
// Base: #FAFBFC (near-white) · Surface: #FFFFFF · Ink: #0B1220
// Signature accent: #00B389 (deep teal) with #00E5FF electric-cyan highlight
// Typography: Outfit (headings) / IBM Plex Sans (body) / JetBrains Mono (labels)
// ============================================================

const LandingPage = () => {
  const { token, loading } = useAuth();
  const navigate = useNavigate();
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, -80]);

  useEffect(() => {
    if (!loading && token) navigate("/app/dashboard", { replace: true });
  }, [token, loading, navigate]);

  useEffect(() => {
    const onMove = (e) => {
      setMouse({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFBFC] text-[#0B1220] antialiased overflow-x-hidden font-body">
      <SEO
        description="The leading hospice audit defense platform. AI-powered LCD compliance scoring, eligibility assessments, ICD-10 coding, and HIPAA-compliant documentation. Protect your hospice from Medicare audits with automated defense packages."
        path="/"
      />
      <AmbientBackground mouse={mouse} />
      <TopNav />

      <main className="relative z-10">
        <Hero heroY={heroY} />
        <TrustStrip />
        <DemoVideo />
        <ControlBand />
        <BentoFeatures />
        <HowItWorks />
        <FinalCTA />
      </main>

      <Footer />
    </div>
  );
};

// ============================================================
// AMBIENT BACKGROUND — soft grid + pastel orbs (light theme)
// ============================================================
const AmbientBackground = ({ mouse }) => (
  <div className="fixed inset-0 z-0 pointer-events-none">
    {/* Subtle grid */}
    <div
      className="absolute inset-0 opacity-[0.5]"
      style={{
        backgroundImage:
          "linear-gradient(to right, #00000010 1px, transparent 1px), linear-gradient(to bottom, #00000010 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    />
    {/* Radial fade */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse at center, transparent 20%, #FAFBFC 80%)",
      }}
    />
    {/* Parallax orbs (soft, pastel) */}
    <div
      className="absolute w-[600px] h-[600px] rounded-full blur-[130px] transition-transform duration-[1500ms] ease-out"
      style={{
        left: `${mouse.x * 25}%`,
        top: `${5 + mouse.y * 10}%`,
        background: "rgba(0, 179, 137, 0.18)",
      }}
    />
    <div
      className="absolute bottom-[15%] right-[5%] w-[500px] h-[500px] rounded-full blur-[130px] transition-transform duration-[2000ms] ease-out"
      style={{
        background: "rgba(0, 229, 255, 0.15)",
        right: `${5 + mouse.x * 15}%`,
      }}
    />
  </div>
);

// ============================================================
// TOP NAV — sticky glass
// ============================================================
const TopNav = () => (
  <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-slate-200/60">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3.5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00B389] to-[#00E5FF] flex items-center justify-center shadow-[0_4px_14px_-3px_rgba(0,179,137,0.5)]">
          <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-[#0B1220] text-base tracking-tight">
            HospiceGuard
          </div>
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500">
            Audit Defense
          </div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
        <a
          href="#features"
          className="hover:text-[#00B389] transition-colors font-medium"
          data-testid="nav-features"
        >
          Platform
        </a>
        <a
          href="#how-it-works"
          className="hover:text-[#00B389] transition-colors font-medium"
          data-testid="nav-how"
        >
          How it works
        </a>
        <a
          href="#demo-video"
          className="hover:text-[#00B389] transition-colors font-medium"
          data-testid="nav-demo"
        >
          Demo
        </a>
        <Link
          to="/pricing"
          className="hover:text-[#00B389] transition-colors font-medium"
          data-testid="nav-pricing"
        >
          Pricing
        </Link>
        <Link
          to="/partnership"
          className="hover:text-[#00B389] transition-colors font-medium"
          data-testid="nav-partnership"
        >
          Partnership
        </Link>
      </nav>
      <div className="flex items-center gap-2">
        <Link
          to="/login"
          className="text-sm text-slate-600 hover:text-[#0B1220] px-4 py-2 transition-colors font-medium"
          data-testid="nav-signin"
        >
          Sign in
        </Link>
        <TryFreeButton source="landing-nav" variant="nav" testid="nav-try-free" label="Try free — no login" />
        <Link
          to="/register"
          className="hidden lg:inline-flex text-sm bg-[#0B1220] hover:bg-[#00B389] text-white font-medium px-5 py-2 rounded-full transition-colors items-center gap-1.5"
          data-testid="nav-start-trial"
        >
          Start free
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  </header>
);

// ============================================================
// DEMO VIDEO — 5-minute live product walkthrough
// ============================================================
const DemoVideo = () => (
  <section id="demo-video" className="relative py-24 lg:py-32">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-2xl mx-auto mb-10"
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-[#00B389]/30 bg-[#00B389]/5 px-4 py-1.5 text-xs font-mono uppercase tracking-[0.15em] text-[#00B389]">
          <Sparkles className="w-3.5 h-3.5" />
          Live product tour
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-[#0B1220] mt-5">
          See HospiceGuard in action
        </h2>
        <p className="mt-4 text-slate-600 text-base leading-relaxed">
          A guided walk through the real platform — SSVI intelligence, pre-audit checks,
          AI documentation, and one-click audit defense, all on live data.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-24px_rgba(11,18,32,0.4)] bg-white"
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200">
          <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
          <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
          <span className="w-3 h-3 rounded-full bg-[#28C840]" />
          <div className="ml-3 flex-1 max-w-sm">
            <div className="rounded-md bg-white border border-slate-200 px-3 py-1 text-[11px] text-slate-400 font-mono truncate">
              app.hospiceguard.com/dashboard
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#00B389]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00B389] animate-pulse" />
            Live
          </span>
        </div>

        <video
          controls
          preload="none"
          playsInline
          poster="/demo-poster.jpg"
          className="w-full aspect-video bg-black block"
          data-testid="landing-demo-video"
        >
          <source src="/HospiceGuard_LiveApp_Demo.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </motion.div>

      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-sm text-slate-500">
        <span className="inline-flex items-center gap-1.5"><Play className="w-4 h-4 text-[#00B389]" /> 5-minute walkthrough</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#00B389]" /> Real product, real data</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#00B389]" /> Narrated tour, sound on</span>
      </div>
    </div>
  </section>
);

// ============================================================
// HERO — asymmetrical split, floating dashboard HUD
// ============================================================
const Hero = ({ heroY }) => {
  const [variant] = useState(() => getVariant("hero_headline", ["A", "B"]));
  useEffect(() => {
    trackAb("hero_headline", variant, "view");
  }, [variant]);
  const onCta = () => trackAb("hero_headline", variant, "cta_click");
  return (
  <motion.section
    style={{ y: heroY }}
    className="relative pt-16 pb-24 lg:pt-24 lg:pb-32"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      {/* Left: bold typography */}
      <div className="lg:col-span-7 space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm border border-slate-200 text-[#00B389] text-xs font-mono"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#00B389] animate-pulse" />
          <span className="tracking-[0.2em] font-semibold">
            HIPAA · FHIR · SOC-2 READY
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[0.95] text-[#0B1220]"
        >
          {variant === "B" ? (
            <>
              Hospice compliance you can{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#00B389] via-[#00A3E0] to-[#00E5FF] bg-clip-text text-transparent">
                  defend — and prove.
                </span>
                <span
                  className="absolute -inset-x-1 bottom-2 h-4 -z-0 blur-2xl"
                  style={{ background: "rgba(0, 179, 137, 0.35)" }}
                />
              </span>
            </>
          ) : (
            <>
              Protect every claim.{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#00B389] via-[#00A3E0] to-[#00E5FF] bg-clip-text text-transparent">
                  Pass every audit.
                </span>
                <span
                  className="absolute -inset-x-1 bottom-2 h-4 -z-0 blur-2xl"
                  style={{ background: "rgba(0, 179, 137, 0.35)" }}
                />
              </span>
            </>
          )}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-lg text-slate-600 max-w-xl leading-relaxed"
        >
          A clinical-grade command center for hospice compliance. Real-time LCD
          scoring, audit-proof defense packages, and voice-to-compliant notes —
          all in one HIPAA-secure platform.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12 }}
          className="flex items-start gap-2 text-sm text-slate-500 max-w-xl"
          data-testid="hero-trust-line"
        >
          <CheckCircle2 className="w-4 h-4 text-[#00B389] mt-0.5 shrink-0" />
          <span>
            Clinician-approved by design: every draft is grounded in your own
            documentation, fully editable, and signed off by your team before it's
            used — never fabricated, always auditable.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex flex-wrap items-center gap-3 pt-2"
        >
          <TryFreeButton source="landing-hero" variant="primary" testid="hero-cta-try-free" />
          <Link
            to="/register"
            className="bg-white border border-slate-200 hover:border-slate-400 text-[#0B1220] px-7 py-3.5 rounded-full text-sm transition-colors inline-flex items-center gap-2 shadow-sm"
            data-testid="hero-cta-start-trial"
            onClick={onCta}
          >
            Start free trial
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/partnership"
            className="text-[#0B1220] hover:text-[#00B389] px-3 py-3.5 text-sm transition-colors inline-flex items-center gap-2"
            data-testid="hero-cta-book-demo"
            onClick={onCta}
          >
            <Play className="w-4 h-4" />
            Book a demo
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-wrap items-center gap-6 pt-4 text-xs font-mono text-slate-500 uppercase tracking-[0.15em]"
        >
          <span className="flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-[#00B389]" />
            End-to-end encryption
          </span>
          <span className="flex items-center gap-2">
            <Fingerprint className="w-3.5 h-3.5 text-[#00B389]" />
            PHI under BAA
          </span>
          <span className="flex items-center gap-2">
            <Database className="w-3.5 h-3.5 text-[#00B389]" />
            FHIR native
          </span>
        </motion.div>
      </div>

      {/* Right: floating dashboard HUD */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 0.2 }}
        className="lg:col-span-5 relative"
      >
        <HeroDashboardMockup />
      </motion.div>
    </div>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5 }}
      className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-slate-400"
    >
      <span className="text-[10px] font-mono uppercase tracking-[0.3em]">
        Scroll
      </span>
      <ChevronDown className="w-4 h-4 animate-bounce" />
    </motion.div>
  </motion.section>
  );
};

// Floating dashboard HUD — pure JSX/SVG on light theme
const HeroDashboardMockup = () => (
  <div className="relative">
    {/* Glow behind card */}
    <div
      className="absolute -inset-8 blur-3xl"
      style={{
        background:
          "linear-gradient(to bottom right, rgba(0,179,137,0.18), transparent, rgba(0,229,255,0.15))",
      }}
    />

    <motion.div
      animate={{ y: [-4, 4, -4] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(11,18,32,0.15)]"
    >
      {/* Card header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
        <div className="text-[10px] font-mono text-slate-500 tracking-[0.15em] uppercase">
          Audit Defense · Live
        </div>
        <div className="w-2 h-2 rounded-full bg-[#00B389] animate-pulse" />
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {/* Metric row */}
        <div className="grid grid-cols-3 gap-3">
          <MetricTile label="LCD Score" value="97" delta="+4" positive />
          <MetricTile label="Notes Reviewed" value="1,284" delta="24h" />
          <MetricTile label="Risk" value="LOW" delta="stable" positive />
        </div>

        {/* Chart */}
        <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">
              Compliance Trend · 30d
            </div>
            <div className="text-[10px] text-[#00B389] font-mono font-semibold">
              ▲ 12.4%
            </div>
          </div>
          <MiniChart />
        </div>

        {/* Active checks */}
        <div className="space-y-2">
          {[
            { label: "F2F encounter documented", status: "pass" },
            { label: "Recert prognosis narrative", status: "pass" },
            { label: "IDG meeting minutes attached", status: "warn" },
          ].map((row, i) => (
            <div
              key={i}
              className="flex items-center justify-between text-xs bg-slate-50/70 rounded-lg px-3 py-2 border border-slate-100"
            >
              <span className="text-[#0B1220] font-medium">{row.label}</span>
              <StatusPill status={row.status} />
            </div>
          ))}
        </div>
      </div>
    </motion.div>

    {/* Floating badge */}
    <motion.div
      animate={{ y: [4, -4, 4] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -top-3 -right-3 bg-[#0B1220] text-white font-mono text-[10px] tracking-[0.15em] uppercase font-bold px-3 py-1.5 rounded-full shadow-[0_8px_20px_-4px_rgba(11,18,32,0.4)] flex items-center gap-1.5"
    >
      <Sparkles className="w-3 h-3 text-[#00E5FF]" />
      Real-time scoring
    </motion.div>
  </div>
);

const MetricTile = ({ label, value, delta, positive }) => (
  <div className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm">
    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">
      {label}
    </div>
    <div className="mt-1 flex items-baseline gap-1.5">
      <div className="text-xl font-heading font-bold text-[#0B1220] tabular-nums">
        {value}
      </div>
      <div
        className={`text-[10px] font-mono font-semibold ${
          positive ? "text-[#00B389]" : "text-slate-500"
        }`}
      >
        {delta}
      </div>
    </div>
  </div>
);

const MiniChart = () => {
  const points = [22, 28, 24, 34, 32, 42, 38, 48, 46, 54, 58, 62, 68, 74, 78];
  const max = Math.max(...points);
  const step = 100 / (points.length - 1);
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(
          60 -
          (p / max) * 55
        ).toFixed(1)}`
    )
    .join(" ");
  const area = `${path} L 100 60 L 0 60 Z`;
  return (
    <svg
      viewBox="0 0 100 60"
      className="w-full h-16"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="chartFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#00B389" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#00B389" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#chartFill)" />
      <path
        d={path}
        fill="none"
        stroke="#00B389"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pass: {
      text: "PASS",
      color: "text-emerald-700",
      bg: "bg-emerald-50 border-emerald-200",
    },
    warn: {
      text: "REVIEW",
      color: "text-amber-700",
      bg: "bg-amber-50 border-amber-200",
    },
    fail: {
      text: "FAIL",
      color: "text-red-700",
      bg: "bg-red-50 border-red-200",
    },
  };
  const s = map[status] || map.pass;
  return (
    <span
      className={`${s.bg} ${s.color} px-2 py-0.5 rounded-full text-[9px] font-mono tracking-[0.15em] border font-semibold`}
    >
      {s.text}
    </span>
  );
};

// ============================================================
// TRUST STRIP
// ============================================================
const TrustStrip = () => (
  <section className="relative border-y border-slate-200/70 py-10 bg-white/40 backdrop-blur">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="text-center text-xs font-mono uppercase tracking-[0.25em] text-slate-500 mb-6">
        Built for the clinicians who don&apos;t have time for busywork
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
        {[
          "Independent Hospices",
          "Multi-State Networks",
          "Home Health + Palliative",
          "Consultants & Auditors",
          "EMR Partners",
        ].map((label) => (
          <div
            key={label}
            className="text-sm font-heading font-semibold text-slate-700 flex items-center gap-2"
          >
            <div className="w-1 h-1 rounded-full bg-[#00B389]" />
            {label}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// CONTROL BAND — "How your team stays in control" (trust reinforcement)
// ============================================================
const ControlBand = () => {
  const steps = [
    { n: "01", icon: Database, title: "Grounded in your records", body: "Every draft is built only from your own charts, visits and notes — nothing invented." },
    { n: "02", icon: FileCheck, title: "You edit everything", body: "Clinicians review and adjust each line before anything is finalized." },
    { n: "03", icon: CheckCircle2, title: "Your team approves", body: "Nothing is used until a qualified reviewer signs off. You stay in control." },
    { n: "04", icon: Shield, title: "Fully auditable", body: "Every action is logged with a timestamped trail you can hand to a surveyor." },
  ];
  return (
    <section id="stay-in-control" className="relative py-24 lg:py-28 bg-[#0B1220]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-mono uppercase tracking-[0.15em] text-[#00E5FF]">
            <Shield className="w-3.5 h-3.5" /> Human-in-the-loop
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white mt-5">
            How your team stays in control
          </h2>
          <p className="mt-4 text-slate-300 text-base leading-relaxed">
            HospiceGuard does the heavy lifting, but your clinicians make every call.
            From first draft to sign-off, the workflow is built to be defensible.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6"
              data-testid={`control-step-${i + 1}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#00B389]/15 text-[#00E5FF]">
                  <s.icon className="w-5 h-5" />
                </span>
                <span className="font-mono text-xs text-slate-500">{s.n}</span>
              </div>
              <h3 className="font-heading font-bold text-white text-lg">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 w-5 h-5 text-white/20" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// BENTO FEATURES — asymmetric 12-col grid
// ============================================================
const BentoFeatures = () => (
  <section id="features" className="relative py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHeader
        overline="Platform"
        title={
          <>
            Every step of the audit,{" "}
            <span className="bg-gradient-to-r from-[#00B389] to-[#00E5FF] bg-clip-text text-transparent">
              automated.
            </span>
          </>
        }
        subtitle="From patient admission to defense-ready packages — one platform, six specialized workflows, every output clinician-approved."
      />

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
        <BentoCard
          className="md:col-span-8 min-h-[280px]"
          icon={Brain}
          tag="Core"
          title="Audit-ready defense packages in minutes"
          body="Feed us the chart, we return an audit-ready defense package with LCD citations, prognosis narrative, and rebuttals to every ADR sub-question — in under 90 seconds."
          testid="feature-ai-defense"
          accent
        >
          <div className="mt-6 flex items-center gap-4 text-xs font-mono text-slate-600">
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              &lt;90s generation
            </span>
          </div>
        </BentoCard>

        <BentoCard
          className="md:col-span-4 min-h-[280px]"
          icon={FileCheck}
          tag="Live"
          title="Real-time LCD scoring"
          body="Every note graded on the fly against your MAC's local coverage determinations. Fix gaps before they land in an audit."
          testid="feature-lcd-scoring"
        />

        <BentoCard
          className="md:col-span-4 min-h-[240px]"
          icon={Mic}
          tag="Bedside"
          title="Voice-to-compliant notes"
          body="Speak the visit. Get a Medicare-compliant SOAP note with F2F, LCD prompts, and vitals — before you leave the parking lot."
          testid="feature-voice-notes"
        />
        <BentoCard
          className="md:col-span-4 min-h-[240px]"
          icon={Users}
          tag="Team"
          title="IDG auto-prep"
          body="Bi-weekly interdisciplinary summaries assembled from every discipline's notes. Show up to IDG with the whole story, not just yours."
          testid="feature-idg-prep"
        />
        <BentoCard
          className="md:col-span-4 min-h-[240px]"
          icon={Activity}
          tag="Signals"
          title="Risk radar"
          body="Daily digest of the patients most likely to trigger an ADR — ranked by MAC pattern, LOS decline, and documentation drift."
          testid="feature-risk-radar"
        />

        <BentoCard
          className="md:col-span-5 min-h-[220px]"
          icon={Database}
          tag="Infra"
          title="FHIR-native PHI store"
          body="Patient data stored in Google Cloud Healthcare API. BAA-covered, tenant-isolated, break-glass audited."
          testid="feature-fhir-store"
        />
        <BentoCard
          className="md:col-span-7 min-h-[220px]"
          icon={Network}
          tag="Integrations"
          title="Plays nicely with your EMR"
          body="HCHB, MatrixCare, KanTime, Epic — pull chart bundles in, push signed notes back out. No double-entry, no CSV exports."
          testid="feature-emr-integrations"
        >
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {["HCHB", "MatrixCare", "KanTime", "Epic", "FHIR R4"].map((tag) => (
              <span
                key={tag}
                className="text-[10px] font-mono uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 font-semibold"
              >
                {tag}
              </span>
            ))}
          </div>
        </BentoCard>
      </div>
    </div>
  </section>
);

const BentoCard = ({
  className = "",
  icon: Icon,
  tag,
  title,
  body,
  testid,
  accent = false,
  children,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5 }}
    data-testid={testid}
    className={`group relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 hover:border-[#00B389]/40 hover:shadow-[0_20px_40px_-15px_rgba(11,18,32,0.12)] transition-all ${
      accent
        ? "bg-gradient-to-br from-white via-white to-emerald-50/50"
        : ""
    } ${className}`}
  >
    {accent && (
      <div className="absolute inset-0 bg-gradient-to-br from-[#00B389]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    )}
    <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
            accent
              ? "bg-[#00B389] text-white shadow-[0_8px_20px_-4px_rgba(0,179,137,0.4)]"
              : "bg-slate-50 text-[#00B389] border border-slate-100 group-hover:bg-[#00B389] group-hover:text-white group-hover:border-transparent"
          }`}
        >
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 font-semibold">
          {tag}
        </span>
      </div>
      <h3 className="font-heading text-xl lg:text-2xl font-bold text-[#0B1220] tracking-tight leading-snug mb-3">
        {title}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
      {children}
    </div>
  </motion.div>
);

// ============================================================
// HOW IT WORKS — technical timeline
// ============================================================
const HowItWorks = () => {
  const steps = [
    {
      code: "01",
      title: "Ingest",
      body: "Pull the chart bundle from your EMR, upload PDFs, or dictate at the bedside. Every artifact lands in the FHIR-native PHI store.",
    },
    {
      code: "02",
      title: "Analyze",
      body: "Six specialized agents score against LCD, F2F, IDG, aide supervision, prognosis, and medication reconciliation — in parallel.",
    },
    {
      code: "03",
      title: "Recommend",
      body: "Structured findings with fix-it prompts. Not a wall of red — a prioritized to-do list your team can knock out in one afternoon.",
    },
    {
      code: "04",
      title: "Defend",
      body: "When the ADR letter arrives, hit one button. Audit-ready defense package with citations, prognosis narrative, and IDG minutes in under 90 seconds.",
    },
  ];

  return (
    <section
      id="how-it-works"
      className="relative py-24 lg:py-32 bg-white/50 backdrop-blur border-y border-slate-200/60"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          overline="How it works"
          title={
            <>
              Four steps. Zero{" "}
              <span className="bg-gradient-to-r from-[#00B389] to-[#00E5FF] bg-clip-text text-transparent">
                busywork.
              </span>
            </>
          }
          subtitle="Same workflow whether you're a solo consultant or a 200-nurse network."
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6 relative">
          {/* Connecting line */}
          <div
            className="hidden md:block absolute top-8 left-8 right-8 h-px"
            style={{
              background:
                "linear-gradient(to right, transparent, rgba(0, 179, 137, 0.4), transparent)",
            }}
          />
          {steps.map((s, i) => (
            <motion.div
              key={s.code}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative"
            >
              <div className="w-16 h-16 rounded-xl bg-white border border-slate-200 flex items-center justify-center relative z-10 mb-4 shadow-sm">
                <span className="font-mono text-[#00B389] text-sm tracking-[0.15em] font-semibold">
                  {s.code}
                </span>
              </div>
              <h3 className="font-heading text-xl font-bold text-[#0B1220] tracking-tight mb-2">
                {s.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// FINAL CTA
// ============================================================
const FinalCTA = () => (
  <section className="relative py-24 lg:py-32">
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <div
        className="relative overflow-hidden rounded-3xl p-10 lg:p-16 text-center"
        style={{
          background:
            "linear-gradient(135deg, #0B1220 0%, #072a35 100%)",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(0, 179, 137, 0.35), transparent 60%)",
          }}
        />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-mono uppercase tracking-[0.2em] mb-6 font-semibold">
            <Zap className="w-3 h-3" />
            14-day free trial
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-4 text-white">
            The audit is coming.
            <br />
            <span className="bg-gradient-to-r from-[#00E5FF] to-[#00B389] bg-clip-text text-transparent">
              Be the hospice that&apos;s ready.
            </span>
          </h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-8 leading-relaxed">
            Spin up your workspace in 60 seconds. No credit card. Full platform
            access for two weeks.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <TryFreeButton source="landing-final" variant="primary" testid="final-cta-try-free" className="px-8 py-4" />
            <Link
              to="/register"
              className="bg-white/10 backdrop-blur border border-white/20 hover:border-white/50 text-white font-semibold px-8 py-4 rounded-full text-sm transition-colors inline-flex items-center gap-2"
              data-testid="final-cta-start-trial"
            >
              Create free account
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/partnership"
              className="text-white/80 hover:text-white px-5 py-4 text-sm transition-colors inline-flex items-center gap-2"
              data-testid="final-cta-partnership"
            >
              <Handshake className="w-4 h-4" />
              Discuss partnership
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============================================================
// FOOTER
// ============================================================
const Footer = () => (
  <footer className="relative z-10 border-t border-slate-200 py-12 lg:py-16 bg-white/60 backdrop-blur">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00B389] to-[#00E5FF] flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-bold text-[#0B1220]">
              HospiceGuard
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            HIPAA-compliant hospice audit defense platform.
          </p>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-3 font-semibold">
            Product
          </div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>
              <a
                href="#features"
                className="hover:text-[#00B389] transition-colors"
              >
                Features
              </a>
            </li>
            <li>
              <Link
                to="/pricing"
                className="hover:text-[#00B389] transition-colors"
                data-testid="footer-pricing"
              >
                Pricing
              </Link>
            </li>
            <li>
              <Link
                to="/register"
                className="hover:text-[#00B389] transition-colors"
                data-testid="footer-register"
              >
                Start free
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-3 font-semibold">
            Company
          </div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>
              <Link
                to="/partnership"
                className="hover:text-[#00B389] transition-colors"
                data-testid="footer-partnership"
              >
                Partnership
              </Link>
            </li>
            <li>
              <Link
                to="/login"
                className="hover:text-[#00B389] transition-colors"
                data-testid="footer-login"
              >
                Sign in
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-3 font-semibold">
            Contact
          </div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>
              <a
                href="mailto:nsarpong@goodwinliving.org"
                className="hover:text-[#00B389] transition-colors"
                data-testid="footer-email"
              >
                nsarpong@goodwinliving.org
              </a>
            </li>
            <li>
              <a
                href="tel:+15713296603"
                className="hover:text-[#00B389] transition-colors"
                data-testid="footer-phone"
              >
                (571) 329-6603
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="text-xs text-slate-500">
          © 2026 HospiceGuard · A Goodwin Living initiative
        </div>
        <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-[0.15em] text-slate-500 font-semibold">
          <span className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            All systems operational
          </span>
        </div>
      </div>
    </div>
  </footer>
);

// ============================================================
// SHARED — section header
// ============================================================
const SectionHeader = ({ overline, title, subtitle }) => (
  <div className="max-w-3xl">
    <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#00B389] mb-4 font-semibold">
      {overline}
    </div>
    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-[#0B1220]">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-base text-slate-600 leading-relaxed">
        {subtitle}
      </p>
    )}
  </div>
);

export default LandingPage;
